import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useToast } from "../providers/ToastProvider";
import api from "../../services/api";
import Dialog from "./Dialog";
import { categoryEmojis } from "../../utils/constants";

export default function CourseTaskManager() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { showToast } = useToast();

  const [course, setCourse] = useState<any>(null);
  const [tasks, setTasks] = useState<any[]>([]);
  const [businessImpactCategories, setBusinessImpactCategories] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const [isTaskModalOpen, setIsTaskModalOpen] = useState(false);
  const [editingTask, setEditingTask] = useState<any>(null);
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    type: "TEXT",
    topicId: "",
    points: 100,
    business_impact_category_id: "",
    expected_outcome: "",
    student_benefits: [""] as string[],
    coach_recommendation: "",
    common_mistakes: [] as string[],
    effort_minutes: 5,
    difficulty: "Beginner",
    prompt: ""
  });

  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);

  const fetchCourseDetails = async () => {
    try {
      setIsLoading(true);
      const [res, catRes] = await Promise.all([
        api.get(`/courses/${id}`),
        api.get('/business-impact-categories')
      ]);
      setCourse(res.data);
      setTasks(res.data.tasks || []);
      setBusinessImpactCategories(catRes.data || []);
    } catch (err) {
      console.error("Failed to fetch course details:", err);
      showToast("Error", "Could not load course details.", "error");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (id) fetchCourseDetails();
  }, [id]);

  const openAddModal = () => {
    setEditingTask(null);
    setFormData({
      title: "",
      description: "",
      type: "TEXT",
      topicId: course?.topics?.[0]?.id || "",
      points: 100,
      business_impact_category_id: "",
      expected_outcome: "",
      student_benefits: [""],
      coach_recommendation: "",
      common_mistakes: [""],
      effort_minutes: 5,
      difficulty: "Beginner",
      prompt: ""
    });
    setIsTaskModalOpen(true);
  };

  const openEditModal = (task: any) => {
    setEditingTask(task);
    setFormData({
      title: task.title,
      description: task.description || "",
      type: task.type || "TEXT",
      topicId: task.topicId || "",
      points: task.points || 100,
      business_impact_category_id: task.business_impact_category_id || "",
      expected_outcome: task.expected_outcome || "",
      student_benefits: task.student_benefits?.length ? task.student_benefits : [""],
      coach_recommendation: task.coach_recommendation || "",
      common_mistakes: task.common_mistakes?.length ? task.common_mistakes : [""],
      effort_minutes: task.effort_minutes || 5,
      difficulty: task.difficulty || "Beginner",
      prompt: task.task_prompt?.prompt || ""
    });
    setIsTaskModalOpen(true);
  };

  const closeTaskModal = () => {
    setIsTaskModalOpen(false);
    setEditingTask(null);
  };

  const handleSaveTask = async () => {
    try {
      // Clean array fields by removing empty strings to prevent Laravel validation errors
      const cleanPayload = {
        ...formData,
        student_benefits: formData.student_benefits.filter((b: string) => b.trim() !== ""),
        common_mistakes: formData.common_mistakes.filter((m: string) => m.trim() !== ""),
      };

      if (editingTask) {
        await api.patch(`/tasks/${editingTask.id}`, cleanPayload);
        showToast("Success", "Task updated successfully.");
      } else {
        await api.post(`/tasks`, { ...cleanPayload, courseId: id });
        showToast("Success", "Task created successfully.");
      }
      closeTaskModal();
      fetchCourseDetails();
    } catch (err: any) {
      console.error("Failed to save task:", err);
      if (err.response?.data?.errors) {
        // Extract the first validation error message
        const firstErrorKey = Object.keys(err.response.data.errors)[0];
        const firstErrorMessage = err.response.data.errors[firstErrorKey][0];
        showToast("Validation Error", firstErrorMessage, "error");
      } else {
        showToast("Error", err.response?.data?.message || "Could not save the task.", "error");
      }
    }
  };

  const confirmDeleteTask = async () => {
    if (!deleteConfirmId) return;
    try {
      await api.delete(`/tasks/${deleteConfirmId}`);
      showToast("Deleted", "Task has been permanently deleted.");
      setDeleteConfirmId(null);
      fetchCourseDetails();
    } catch (err: any) {
      console.error("Failed to delete task:", err);
      showToast("Error", err.response?.data?.details || "Could not delete task.", "error");
    }
  };

  const handleSmartPaste = (e: React.ClipboardEvent<HTMLInputElement>, fieldName: 'student_benefits' | 'common_mistakes', currentIndex: number) => {
    const pastedText = e.clipboardData.getData('text');
    if (pastedText.includes('\n') || pastedText.includes(',')) {
      e.preventDefault();
      const rawItems = pastedText.split(/[\n,]+/);
      const cleanedItems = rawItems
        .map(item => {
          let cleaned = item.trim();
          cleaned = cleaned.replace(/^[\s\-\*\•\d\.\)]+\s*/, '');
          cleaned = cleaned.replace(/^[\p{Emoji}\s]+/gu, '');
          cleaned = cleaned.replace(/[\p{Emoji}\s]+$/gu, '');
          return cleaned.trim();
        })
        .filter(item => item.length > 0);

      if (cleanedItems.length > 0) {
        const newList = [...formData[fieldName]];
        newList.splice(currentIndex, 1, ...cleanedItems);
        setFormData({ ...formData, [fieldName]: newList });
      }
    }
  };

  const copyTaskSampleJsonToClipboard = async () => {
    const sample = {
      _AI_INSTRUCTIONS_: [
        "Generate a task for this course.",
        "The task description must be 'Modern Malayalam + Simple English'.",
        "For 'business_impact_category' and 'topicName', choose from the existing lists OR create new names.",
        "Provide the final response STRICTLY as raw, valid JSON text matching this exact structure. Do not use markdown blocks (```json) or conversational text so I can copy it directly."
      ],
      title: "Sample Task Title",
      description: "Sample description detailing what the student should do in modern Malayalam and simple English.",
      type: "TEXT",
      topicName: "",
      currently_available_topics_to_link: course?.topics?.map((t: any) => t.name) || [],
      points: 100,
      business_impact_category: "Sales Booster",
      currently_available_business_impact_categories: businessImpactCategories.map((c: any) => c.name),
      effort_minutes: 10,
      difficulty: "Beginner",
      prompt: "Sample AI prompt if required"
    };
        const textToCopy = JSON.stringify(sample, null, 2);
      try {
        if (navigator.clipboard && window.isSecureContext) {
          await navigator.clipboard.writeText(textToCopy);
        } else {
          // Fallback for insecure HTTP contexts
          const textArea = document.createElement("textarea");
          textArea.value = textToCopy;
          textArea.style.position = "fixed";
          textArea.style.left = "-999999px";
          textArea.style.top = "-999999px";
          document.body.appendChild(textArea);
          textArea.focus();
          textArea.select();
          const successful = document.execCommand('copy');
          textArea.remove();
          if (!successful) throw new Error("document.execCommand failed");
        }
        showToast("Success", "AI JSON template copied to clipboard", "success");
      } catch (err) {
        console.error("Clipboard Copy Error:", err);
        showToast("Error", "Failed to copy to clipboard", "error");
      }
    };

  const handleTaskPasteFromClipboard = async () => {
    try {
      let text = "";
      if (navigator.clipboard && window.isSecureContext) {
        try {
          text = await navigator.clipboard.readText();
        } catch (e) {
          text = window.prompt("Your browser blocked direct clipboard access. Please paste (Ctrl+V) the AI JSON here:") || "";
        }
      } else {
        text = window.prompt("Your browser blocked direct clipboard access. Please paste (Ctrl+V) the AI JSON here:") || "";
      }
      
      if (!text) {
        showToast("Error", "Clipboard/Input is empty", "error");
        return;
      }
      
      // Auto-strip any markdown codeblocks or conversational text around the JSON
      const jsonMatch = text.match(/\{[\s\S]*\}/);
      const cleanedText = jsonMatch ? jsonMatch[0] : text;
      
      const json = JSON.parse(cleanedText);
      
      let mappedCategoryId = json.business_impact_category_id;
      const categorySearch = (json.business_impact_category_id || json.business_impact_category || json.categoryName || "").toString().trim().toLowerCase();
      
      if (categorySearch) {
        const existingCat = businessImpactCategories.find((c: any) => c.name.toLowerCase() === categorySearch);
        if (existingCat) {
          mappedCategoryId = existingCat.id;
        } else {
           try {
             const rawName = (json.business_impact_category_id || json.business_impact_category || json.categoryName).toString().trim();
             const res = await api.post("/business-impact-categories", { name: rawName });
             if (res.data && res.data.category) {
               setBusinessImpactCategories(prev => [...prev, res.data.category]);
               mappedCategoryId = res.data.category.id;
               showToast("Category Added", `Auto-created impact category "${res.data.category.name}".`, "success");
             }
           } catch (e) {
             console.warn("Could not auto-create impact category");
           }
        }
      }
      
      let mappedTopicId = json.topicId;
      const topicSearch = (json.topicId || json.topicName || "").toString().trim().toLowerCase();
      
      if (topicSearch && course?.topics) {
        const existingTopic = course.topics.find((t: any) => t.name.toLowerCase() === topicSearch);
        if (existingTopic) {
          mappedTopicId = existingTopic.id;
        } else {
           try {
             const rawName = (json.topicId || json.topicName).toString().trim();
             const res = await api.post("/topics", { name: rawName, course_id: course.id });
             if (res.data && res.data.topic) {
               setCourse((prev: any) => ({ ...prev, topics: [...(prev.topics || []), res.data.topic] }));
               mappedTopicId = res.data.topic.id;
               showToast("Topic Added", `Auto-created topic "${res.data.topic.name}".`, "success");
             }
           } catch (e) {
             console.warn("Could not auto-create topic");
           }
        }
      }

      setFormData(prev => ({
        ...prev,
        title: json.title || prev.title,
        description: json.description || prev.description,
        type: json.type || prev.type,
        topicId: mappedTopicId || prev.topicId,
        points: typeof json.points === 'number' ? json.points : prev.points,
        business_impact_category_id: mappedCategoryId || prev.business_impact_category_id,
        effort_minutes: typeof json.effort_minutes === 'number' ? json.effort_minutes : prev.effort_minutes,
        difficulty: json.difficulty || prev.difficulty,
        prompt: json.prompt || prev.prompt
      }));

      showToast("Success", "Task form populated from JSON");
    } catch (err) {
      console.error(err);
      showToast("Error", "Invalid JSON in clipboard or permission denied", "error");
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center py-20">
        <div className="w-10 h-10 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!course) {
    return <div className="py-20 text-center">Course not found.</div>;
  }

  return (
    <div className="space-y-8 animate-in fade-in duration-500 pb-20">
      <div className="flex items-center gap-4">
        <button 
          onClick={() => navigate(-1)}
          className="w-10 h-10 bg-surface-container-low rounded-full flex items-center justify-center hover:bg-surface-container-high transition-colors"
        >
          <span className="material-symbols-outlined text-on-surface-variant">arrow_back</span>
        </button>
        <div>
          <h1 className="text-2xl sm:text-3xl font-black text-on-surface">{course.title}</h1>
          <p className="text-on-surface-variant text-sm mt-1">{course.category?.name || "Uncategorized"}</p>
        </div>
      </div>

      <div className="bg-surface-container-low border border-outline-variant/10 rounded-[2rem] p-6 sm:p-8">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-8">
          <div>
            <h2 className="text-xl font-bold text-on-surface">Curriculum Tasks</h2>
            <p className="text-xs text-on-surface-variant mt-1">Manage the assignments and requirements for this course.</p>
          </div>
          <button 
            onClick={openAddModal}
            className="px-5 py-2.5 bg-primary text-white rounded-xl font-black text-xs uppercase tracking-widest hover:scale-[1.02] transition-all shadow-lg shadow-primary/20 flex items-center gap-2"
          >
            <span className="material-symbols-outlined text-sm">add</span>
            New Task
          </button>
        </div>

        {tasks.length === 0 ? (
          <div className="py-12 text-center bg-surface-container-lowest border border-dashed border-outline-variant/20 rounded-2xl">
            <p className="text-on-surface-variant font-medium text-sm">No tasks have been added to this course yet.</p>
          </div>
        ) : (
          <div className="space-y-4">
            {tasks.map((task) => (
              <div key={task.id} className="p-5 bg-surface-container-lowest border border-outline-variant/10 rounded-2xl flex flex-col md:flex-row gap-4 items-start md:items-center justify-between group hover:border-primary/30 transition-all">
                <div className="space-y-2 flex-1">
                  <div className="flex items-center gap-3">
                    <span className="px-2.5 py-1 bg-surface-container-high text-on-surface-variant rounded-md text-[9px] font-black uppercase tracking-widest border border-outline-variant/5">
                      {task.type}
                    </span>
                    <span className="text-[10px] text-slate-400 font-bold uppercase tracking-widest">
                      {task.points} PTS
                    </span>
                  </div>
                  <h3 className="text-lg font-bold text-on-surface leading-tight group-hover:text-primary transition-colors">{task.title}</h3>
                  <p className="text-sm text-on-surface-variant line-clamp-2">{task.description}</p>
                </div>
                
                <div className="flex flex-wrap items-center gap-2 w-full md:w-auto">
                  <button 
                    onClick={() => navigate(`/tasks/${task.id}`)}
                    className="flex-1 md:flex-none px-4 py-2.5 bg-primary/10 hover:bg-primary/20 text-primary rounded-xl text-xs font-bold transition-colors flex items-center justify-center gap-2"
                  >
                    <span className="material-symbols-outlined text-sm">visibility</span>
                    View
                  </button>
                  <button 
                    onClick={() => openEditModal(task)}
                    className="flex-1 md:flex-none px-4 py-2.5 bg-surface-container-high hover:bg-surface-container-highest text-on-surface rounded-xl text-xs font-bold transition-colors flex items-center justify-center gap-2"
                  >
                    <span className="material-symbols-outlined text-sm">edit</span>
                    Edit
                  </button>
                  <button 
                    onClick={() => setDeleteConfirmId(task.id)}
                    className="flex-1 md:flex-none px-4 py-2.5 bg-red-500/10 hover:bg-red-500/20 text-red-500 rounded-xl text-xs font-bold transition-colors flex items-center justify-center gap-2"
                  >
                    <span className="material-symbols-outlined text-sm">delete</span>
                    Delete
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Task Edit/Add Modal */}
      <Dialog 
        isOpen={isTaskModalOpen}
        onClose={closeTaskModal}
        title={editingTask ? "Edit Task" : "Create New Task"}
        size="md"
        footer={
          <div className="flex gap-3 justify-end">
            <button onClick={closeTaskModal} className="px-5 py-2.5 rounded-xl font-bold text-xs bg-surface-container-high text-on-surface hover:bg-surface-container-highest transition-colors">
              Cancel
            </button>
            <button onClick={handleSaveTask} className="px-5 py-2.5 rounded-xl font-bold text-xs bg-primary text-white hover:bg-primary/90 transition-colors shadow-lg shadow-primary/20">
              {editingTask ? "Save Changes" : "Create Task"}
            </button>
          </div>
        }
      >
        <div className="flex gap-2 justify-end mb-2">
          <button 
            type="button" 
            onClick={copyTaskSampleJsonToClipboard}
            className="text-[10px] font-black uppercase text-primary bg-primary/10 hover:bg-primary/20 px-3 py-2 rounded-lg tracking-widest flex items-center gap-1 transition-colors"
          >
            <span className="material-symbols-outlined text-sm">content_copy</span>
            Copy AI JSON
          </button>
          <button 
            type="button"
            onClick={handleTaskPasteFromClipboard}
            className="text-[10px] font-black uppercase text-on-surface bg-surface-container-highest hover:bg-surface-container-high px-3 py-2 rounded-lg tracking-widest flex items-center gap-1 transition-colors border border-outline-variant/10"
          >
            <span className="material-symbols-outlined text-sm">content_paste</span>
            Paste from Clipboard
          </button>
        </div>
        <div className="space-y-4">
          <div>
            <label className="block text-xs font-bold text-on-surface-variant uppercase tracking-widest mb-1.5">Task Title</label>
            <input 
              type="text" 
              value={formData.title} 
              onChange={e => setFormData({...formData, title: e.target.value})} 
              className="w-full bg-surface-container border border-outline-variant/40 rounded-xl px-4 py-3 text-sm text-on-surface focus:ring-2 focus:ring-primary/40 focus:border-primary/60 outline-none transition-all"
            />
          </div>
          <div>
            <label className="block text-xs font-bold text-on-surface-variant uppercase tracking-widest mb-1.5">Description</label>
            <textarea 
              value={formData.description} 
              onChange={e => setFormData({...formData, description: e.target.value})} 
              className="w-full bg-surface-container border border-outline-variant/40 rounded-xl px-4 py-3 text-sm text-on-surface min-h-[100px] focus:ring-2 focus:ring-primary/40 focus:border-primary/60 outline-none transition-all"
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-bold text-on-surface-variant uppercase tracking-widest mb-1.5">Type</label>
              <select 
                value={formData.type} 
                onChange={e => setFormData({...formData, type: e.target.value})}
                className="w-full bg-surface-container border border-outline-variant/40 rounded-xl px-4 py-3 text-sm text-on-surface focus:ring-2 focus:ring-primary/40 focus:border-primary/60 outline-none transition-all"
              >
                <option value="TEXT">Text Response</option>
                <option value="LINK">Submit Link</option>
                <option value="IMAGE">Upload Image</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-bold text-on-surface-variant uppercase tracking-widest mb-1.5">Points</label>
              <input 
                type="number" 
                value={formData.points} 
                onChange={e => setFormData({...formData, points: parseInt(e.target.value) || 0})} 
                className="w-full bg-surface-container border border-outline-variant/40 rounded-xl px-4 py-3 text-sm text-on-surface focus:ring-2 focus:ring-primary/40 focus:border-primary/60 outline-none transition-all"
              />
            </div>
          </div>
          <div>
            <label className="block text-xs font-bold text-on-surface-variant uppercase tracking-widest mb-1.5">Business Impact Category *</label>
            <select 
              value={formData.business_impact_category_id} 
              onChange={e => setFormData({...formData, business_impact_category_id: e.target.value})}
              className="w-full bg-surface-container border border-outline-variant/40 rounded-xl px-4 py-3 text-sm text-on-surface focus:ring-2 focus:ring-primary/40 focus:border-primary/60 outline-none transition-all"
              required
            >
              <option value="">-- Select Category --</option>
              {businessImpactCategories.map((c: any) => {

                return (
                  <option key={c.id} value={c.id}>
                    {categoryEmojis[c.name] ? `${categoryEmojis[c.name]} ` : ''}{c.name}
                  </option>
                );
              })}
            </select>
          </div>
          <div className="hidden">
            <label className="block text-xs font-bold text-on-surface-variant uppercase tracking-widest mb-1.5">Expected Business Outcome *</label>
            <textarea 
              value={formData.expected_outcome} 
              onChange={e => setFormData({...formData, expected_outcome: e.target.value})} 
              placeholder="e.g. Customers can now find your business on Google Maps."
              className="w-full bg-surface-container border border-outline-variant/40 rounded-xl px-4 py-3 text-sm text-on-surface min-h-[80px] focus:ring-2 focus:ring-primary/40 focus:border-primary/60 outline-none transition-all"
              
            />
          </div>
          <div className="hidden">
            <label className="block text-xs font-bold text-on-surface-variant uppercase tracking-widest mb-1.5">Student Benefits</label>
            <div className="space-y-2 mb-2">
              {formData.student_benefits.map((benefit, idx) => (
                <div key={idx} className="flex items-center gap-2">
                  <span className="text-green-500">✅</span>
                  <input 
                    type="text" 
                    value={benefit} 
                    onChange={e => {
                      const newBenefits = [...formData.student_benefits];
                      newBenefits[idx] = e.target.value;
                      setFormData({...formData, student_benefits: newBenefits});
                    }} 
                    onPaste={e => handleSmartPaste(e, 'student_benefits', idx)}
                    placeholder="e.g. More Visibility"
                    className="flex-1 bg-surface-container border border-outline-variant/40 rounded-xl px-4 py-2 text-sm text-on-surface focus:ring-2 focus:ring-primary/40 outline-none"
                  />
                  <button 
                    onClick={() => {
                      const newBenefits = formData.student_benefits.filter((_, i) => i !== idx);
                      setFormData({...formData, student_benefits: newBenefits});
                    }}
                    className="w-8 h-8 flex items-center justify-center text-red-500 bg-red-500/10 rounded-xl"
                  >
                    <span className="material-symbols-outlined text-sm">close</span>
                  </button>
                </div>
              ))}
            </div>
            <button 
              onClick={() => setFormData({...formData, student_benefits: [...formData.student_benefits, ""]})}
              className="text-xs font-bold text-primary hover:underline flex items-center gap-1"
            >
              <span className="material-symbols-outlined text-[14px]">add</span> Add Benefit
            </button>
          </div>
          <div className="hidden">
            <label className="block text-xs font-bold text-on-surface-variant uppercase tracking-widest mb-1.5">👨‍🏫 Joseph's Recommendation</label>
            <textarea 
              value={formData.coach_recommendation} 
              onChange={e => setFormData({...formData, coach_recommendation: e.target.value})} 
              placeholder="Message displayed before students start the action..."
              className="w-full bg-surface-container border border-outline-variant/40 rounded-xl px-4 py-3 text-sm text-on-surface min-h-[100px] focus:ring-2 focus:ring-primary/40 focus:border-primary/60 outline-none transition-all"
            />
          </div>
          <div>
            <label className="block text-xs font-bold text-on-surface-variant uppercase tracking-widest mb-1.5 flex items-center gap-1">
              <span className="material-symbols-outlined text-[14px]">smart_toy</span> AI Prompt (Optional)
            </label>
            <p className="text-xs text-slate-500 mb-2">If this task requires the student to use AI, paste the prompt template here.</p>
            <textarea 
              value={formData.prompt} 
              onChange={e => setFormData({...formData, prompt: e.target.value})} 
              placeholder="Paste the prompt template here..."
              className="w-full bg-surface-container font-mono border border-outline-variant/40 rounded-xl px-4 py-3 text-xs text-on-surface min-h-[150px] focus:ring-2 focus:ring-primary/40 focus:border-primary/60 outline-none transition-all resize-y"
            />
          </div>
          <div className="hidden">
            <label className="block text-xs font-bold text-on-surface-variant uppercase tracking-widest mb-1.5">Common Mistakes (Optional)</label>
            <div className="space-y-2 mb-2">
              {formData.common_mistakes.map((mistake, idx) => (
                <div key={idx} className="flex items-center gap-2">
                  <span className="text-red-500">❌</span>
                  <input 
                    type="text" 
                    value={mistake} 
                    onChange={e => {
                      const newMistakes = [...formData.common_mistakes];
                      newMistakes[idx] = e.target.value;
                      setFormData({...formData, common_mistakes: newMistakes});
                    }} 
                    onPaste={e => handleSmartPaste(e, 'common_mistakes', idx)}
                    placeholder="e.g. Wrong business category"
                    className="flex-1 bg-surface-container border border-outline-variant/40 rounded-xl px-4 py-2 text-sm text-on-surface focus:ring-2 focus:ring-primary/40 outline-none"
                  />
                  <button 
                    onClick={() => {
                      const newMistakes = formData.common_mistakes.filter((_, i) => i !== idx);
                      setFormData({...formData, common_mistakes: newMistakes});
                    }}
                    className="w-8 h-8 flex items-center justify-center text-red-500 bg-red-500/10 rounded-xl"
                  >
                    <span className="material-symbols-outlined text-sm">close</span>
                  </button>
                </div>
              ))}
            </div>
            <button 
              onClick={() => setFormData({...formData, common_mistakes: [...formData.common_mistakes, ""]})}
              className="text-xs font-bold text-primary hover:underline flex items-center gap-1"
            >
              <span className="material-symbols-outlined text-[14px]">add</span> Add Mistake
            </button>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-bold text-on-surface-variant uppercase tracking-widest mb-1.5">⏱ Effort</label>
              <select 
                value={formData.effort_minutes} 
                onChange={e => setFormData({...formData, effort_minutes: parseInt(e.target.value) || 5})}
                className="w-full bg-surface-container border border-outline-variant/40 rounded-xl px-4 py-3 text-sm text-on-surface focus:ring-2 focus:ring-primary/40 focus:border-primary/60 outline-none transition-all"
              >
                <option value="5">5 Minutes</option>
                <option value="10">10 Minutes</option>
                <option value="15">15 Minutes</option>
                <option value="30">30 Minutes</option>
                <option value="60">1 Hour</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-bold text-on-surface-variant uppercase tracking-widest mb-1.5">Difficulty</label>
              <select 
                value={formData.difficulty} 
                onChange={e => setFormData({...formData, difficulty: e.target.value})}
                className="w-full bg-surface-container border border-outline-variant/40 rounded-xl px-4 py-3 text-sm text-on-surface focus:ring-2 focus:ring-primary/40 focus:border-primary/60 outline-none transition-all"
              >
                <option value="Beginner">🟢 Beginner</option>
                <option value="Intermediate">🟡 Intermediate</option>
                <option value="Advanced">🔴 Advanced</option>
              </select>
            </div>
          </div>
          <div>
            <label className="block text-xs font-bold text-on-surface-variant uppercase tracking-widest mb-1.5">Topic Link (Optional)</label>
            <select 
              value={formData.topicId} 
              onChange={e => setFormData({...formData, topicId: e.target.value})}
              className="w-full bg-surface-container border border-outline-variant/40 rounded-xl px-4 py-3 text-sm text-on-surface focus:ring-2 focus:ring-primary/40 focus:border-primary/60 outline-none transition-all"
            >
              <option value="">-- No Topic --</option>
              {course?.topics?.map((t: any) => (
                <option key={t.id} value={t.id}>{t.name}</option>
              ))}
            </select>
          </div>
        </div>
      </Dialog>

      {/* STRONG WARNING DELETE CONFIRMATION MODAL */}
      <Dialog 
        isOpen={deleteConfirmId !== null} 
        onClose={() => setDeleteConfirmId(null)} 
        title="⚠️ DANGER: Confirm Task Deletion"
        size="sm"
        footer={
          <div className="flex gap-4 justify-end w-full">
            <button 
              onClick={() => setDeleteConfirmId(null)} 
              className="px-6 py-3.5 bg-surface-container-high text-on-surface rounded-2xl font-black text-xs uppercase tracking-widest hover:bg-surface-container-highest transition-colors flex-1"
            >
              Keep Task
            </button>
            <button 
              onClick={confirmDeleteTask} 
              className="px-6 py-3.5 bg-red-600 text-white rounded-2xl font-black text-xs uppercase tracking-widest hover:bg-red-700 transition-colors shadow-lg shadow-red-500/30 flex-1 flex items-center justify-center gap-2"
            >
              <span className="material-symbols-outlined text-sm">warning</span>
              Yes, Delete It
            </button>
          </div>
        }
      >
        <div className="text-red-500 font-medium space-y-4 bg-red-500/10 p-4 rounded-xl border border-red-500/20">
          <p className="font-bold text-lg flex items-center gap-2">
             <span className="material-symbols-outlined">error</span>
             WARNING: Irreversible Action
          </p>
          <p className="text-sm">
            Deleting this task will <strong>permanently remove it</strong> from the curriculum. 
          </p>
          <ul className="list-disc pl-5 text-xs opacity-90 space-y-1">
            <li>Any student submissions for this task will be orphaned or deleted.</li>
            <li>Student progress points associated with this task may be affected.</li>
            <li>This action <strong>cannot</strong> be undone.</li>
          </ul>
          <p className="text-sm font-bold pt-2">Are you absolutely sure you want to proceed?</p>
        </div>
      </Dialog>
    </div>
  );
}
